import {
  Component,
  signal,
  computed,
  OnInit,
  inject,
  effect,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { VehicleService } from '../../../services/vehicle.service';
import {
  AnioVehiculoResponse,
  Auto,
  MarcaVehiculoResponse,
  ModeloVehiculoResponse,
  MotorVehiculoResponse,
  SubmodeloVehiculoResponse,
} from '../../../types/vehicle.types';
import { EmailValidationService } from '../../../services/email-validation.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'allas-vehicle-selector',

  imports: [CommonModule, FormsModule, SelectModule],
  template: `
    <div
      class="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm fs-condensed"
      lang="es-HN"
    >
      <!-- Header Section -->
      <header
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4"
      >
        <span class="text-lg font-medium text-gray-900 mb-2 sm:mb-0">
          Agregue su vehículo
        </span>
        <span class="text-sm text-gray-500">Obtenga un ajuste exacto</span>
      </header>

      <!-- Botón para mostrar filtros en mobile -->
      <button
        type="button"
        (click)="toggleMobileFilters()"
        class="xl:hidden w-full mb-4 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-between font-medium"
        aria-label="Mostrar u ocultar filtros de vehículo"
      >
        <span>{{
          isMobileFiltersOpen() ? 'Ocultar filtros' : 'Mostrar filtros'
        }}</span>
        <svg
          class="w-5 h-5 transition-transform"
          [class.rotate-180]="isMobileFiltersOpen()"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <!-- Vehicle Selection Form -->
      <form
        [class]="
          isMobileFiltersOpen()
            ? 'grid grid-cols-1 gap-3 xl:grid-cols-5'
            : 'hidden xl:grid xl:grid-cols-5 gap-3'
        "
        role="form"
        aria-label="Selección de vehículo"
      >
        <ng-container
          *ngTemplateOutlet="
            vehicleField;
            context: {
              step: 1,
              label: 'Año',
              options: yearOptions(),
              optionLabel: 'anio',
              placeholder: 'Seleccionar año',
              disabled: false
            }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            vehicleField;
            context: {
              step: 2,
              label: 'Marca',
              options: makeOptions(),
              optionLabel: 'descripcion',
              placeholder: 'Seleccionar marca',
              disabled: !selectedYear()
            }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            vehicleField;
            context: {
              step: 3,
              label: 'Modelo',
              options: modelOptions(),
              optionLabel: 'descripcion',
              placeholder: 'Seleccionar modelo',
              disabled: !selectedMake()
            }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            vehicleField;
            context: {
              step: 4,
              label: 'Submodelo',
              options: submodelOptions(),
              optionLabel: 'descripcion',
              placeholder: 'Seleccionar submodelo',
              disabled: !selectedModel()
            }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            vehicleField;
            context: {
              step: 5,
              label: 'Motor',
              options: engineOptions(),
              optionLabel: 'descripcion',
              placeholder: 'Seleccionar motor',
              disabled: !selectedSubmodel()
            }
          "
        ></ng-container>
      </form>

      <!-- Save Button Section -->
      <ng-container *ngTemplateOutlet="saveButton"></ng-container>
    </div>

    <!-- Vehicle Field Template -->
    <ng-template
      #vehicleField
      let-step="step"
      let-label="label"
      let-options="options"
      let-optionLabel="optionLabel"
      let-placeholder="placeholder"
      let-disabled="disabled"
    >
      <div class="space-y-1">
        <label
          class="block text-sm font-medium text-gray-700"
          [attr.for]="'vehicle-' + step"
        >
          {{ step }} | {{ label }}
        </label>
        <ng-container [ngSwitch]="step">
          <p-select
            *ngSwitchCase="1"
            [id]="'vehicle-' + step"
            [(ngModel)]="selectedYear"
            [options]="options"
            [filter]="true"
            [optionLabel]="optionLabel"
            [placeholder]="placeholder"
            [disabled]="disabled"
            appendTo="body"
            [style]="{ width: '100%' }"
            class="w-full fs-condensed"
            [attr.aria-label]="placeholder"
          />
          <p-select
            *ngSwitchCase="2"
            [id]="'vehicle-' + step"
            [(ngModel)]="selectedMake"
            [options]="options"
            [filter]="true"
            [optionLabel]="optionLabel"
            [placeholder]="placeholder"
            [disabled]="disabled"
            appendTo="body"
            [style]="{ width: '100%' }"
            class="w-full fs-condensed"
            [attr.aria-label]="placeholder"
          />
          <p-select
            *ngSwitchCase="3"
            [id]="'vehicle-' + step"
            [(ngModel)]="selectedModel"
            [options]="options"
            [filter]="true"
            [optionLabel]="optionLabel"
            [placeholder]="placeholder"
            [disabled]="disabled"
            appendTo="body"
            [style]="{ width: '100%' }"
            class="w-full fs-condensed"
            [attr.aria-label]="placeholder"
          />
          <p-select
            *ngSwitchCase="4"
            [id]="'vehicle-' + step"
            [(ngModel)]="selectedSubmodel"
            [options]="options"
            [filter]="true"
            [optionLabel]="optionLabel"
            [placeholder]="placeholder"
            [disabled]="disabled"
            appendTo="body"
            [style]="{ width: '100%' }"
            class="w-full fs-condensed"
            [attr.aria-label]="placeholder"
          />
          <p-select
            *ngSwitchCase="5"
            [id]="'vehicle-' + step"
            [(ngModel)]="selectedEngine"
            [options]="options"
            [filter]="true"
            [optionLabel]="optionLabel"
            [placeholder]="placeholder"
            [disabled]="disabled"
            appendTo="body"
            [style]="{ width: '100%' }"
            class="w-full fs-condensed"
            [attr.aria-label]="placeholder"
          />
        </ng-container>
      </div>
    </ng-template>

    <!-- Save Button Template -->
    <ng-template #saveButton>
      <ng-container *ngIf="canSaveVehicle()">
        <div class="mt-4 flex justify-end">
          <button
            type="button"
            (click)="saveVehicle()"
            [disabled]="isSaving()"
            [class]="
              isSaving()
                ? 'px-6 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed text-sm font-medium'
                : 'px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium'
            "
            [attr.aria-label]="
              isSaving() ? 'Guardando vehículo' : 'Agregar vehículo al garaje'
            "
          >
            <ng-container *ngIf="isSaving(); else saveText">
              Guardando...
            </ng-container>
            <ng-template #saveText> Agregar vehículo </ng-template>
          </button>
        </div>
      </ng-container>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class VehicleSelector implements OnInit {
  private vehicleService = inject(VehicleService);
  private emailValidationService = inject(EmailValidationService);
  private route = inject(ActivatedRoute);

  // Estado de selección
  selectedYear = signal<AnioVehiculoResponse | null>(null);
  selectedMake = signal<MarcaVehiculoResponse | null>(null);
  selectedModel = signal<ModeloVehiculoResponse | null>(null);
  selectedSubmodel = signal<SubmodeloVehiculoResponse | null>(null);
  selectedEngine = signal<MotorVehiculoResponse | null>(null);
  isSaving = signal<boolean>(false);
  isMobileFiltersOpen = signal<boolean>(false);
  // Señales para la marca, modelo y año PUEDEN VENIR DE LA RUTA
  marca = signal<string | null>(null);
  modelo = signal<string | null>(null);
  anio = signal<number | null>(null);

  // Datos disponibles
  private allYears = signal<AnioVehiculoResponse[]>([]);
  private availableMakes = signal<MarcaVehiculoResponse[]>([]);
  private availableModels = signal<ModeloVehiculoResponse[]>([]);
  private availableSubmodels = signal<SubmodeloVehiculoResponse[]>([]);
  private availableEngines = signal<MotorVehiculoResponse[]>([]);

  // Opciones computadas para los dropdowns
  yearOptions = computed(() => {
    return this.allYears();
  });

  makeOptions = computed(() => {
    return this.availableMakes();
  });

  modelOptions = computed(() => {
    return this.availableModels();
  });

  submodelOptions = computed(() => {
    return this.availableSubmodels();
  });

  engineOptions = computed(() => {
    return this.availableEngines();
  });

  canSaveVehicle = computed(() => {
    return (
      this.selectedYear() &&
      this.selectedMake() &&
      this.selectedModel() &&
      this.selectedSubmodel() &&
      this.selectedEngine()
    );
  });

  constructor() {
    // Effect para cargar marcas cuando se selecciona un año
    effect(() => {
      const year = this.selectedYear();
      if (year) {
        this.loadMakesByYear(year.anio);
        // Reset selections posteriores
        this.selectedMake.set(null);
        this.selectedModel.set(null);
        this.selectedSubmodel.set(null);
        this.selectedEngine.set(null);
      }
    });

    // Effect para cargar modelos cuando se selecciona una marca
    effect(() => {
      const year = this.selectedYear();
      const make = this.selectedMake();
      if (year && make) {
        this.loadModelsByMakeAndYear(year.anio, make.id_marca);
        // Reset selections posteriores
        this.selectedModel.set(null);
        this.selectedSubmodel.set(null);
        this.selectedEngine.set(null);
      }
    });

    // Effect para cargar submodelos cuando se selecciona un modelo
    effect(() => {
      const year = this.selectedYear();
      const make = this.selectedMake();
      const model = this.selectedModel();
      if (year && make && model) {
        this.loadSubmodelsByMakeYearAndModel(model.id_modelo_anio);
      }
    });

    // Effect para cargar motores cuando se selecciona un modelo
    effect(() => {
      const year = this.selectedYear();
      const make = this.selectedMake();
      const model = this.selectedModel();
      const submodel = this.selectedSubmodel();
      if (year && make && model && submodel) {
        this.loadEnginesByMakeYearAndModel(
          model.id_modelo_anio,
          submodel.descripcion
        );
        // Reset selection posterior
        this.selectedEngine.set(null);
      }
    });
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  private setVehicleFromRoute(): void {
    this.route.params.subscribe((params) => {
      this.marca.set(params['marca']);
      this.modelo.set(params['modelo']);
      this.anio.set(params['anio']);
      if (this.anio()) {
        const year = this.allYears().find(
          (year) => Number(year.anio) === Number(this.anio())
        );
        if (year) {
          this.selectedYear.set(year);
        }
      }
    });
  }

  private loadInitialData(): void {
    this.vehicleService.getAnios().subscribe((anios) => {
      this.allYears.set(anios);
      this.setVehicleFromRoute();
    });
  }

  private loadMakesByYear(year: number): void {
    this.vehicleService.getMarcas(year).subscribe((makes) => {
      this.availableMakes.set(makes);
      if (this.marca()) {
        const make = makes.find(
          (make) =>
            make.descripcion.toUpperCase() === this.marca()?.toUpperCase() || ''
        );
        if (make) {
          this.selectedMake.set(make);
          this.loadModelsByMakeAndYear(year, make.id_marca);
        }
      }
    });
  }

  private loadModelsByMakeAndYear(year: number, idMarca: number): void {
    this.vehicleService.getModelos(year, idMarca).subscribe((models) => {
      this.availableModels.set(models);
      if (this.modelo()) {
        const model = models.find(
          (model) =>
            model.descripcion.toUpperCase() === this.modelo()?.toUpperCase() ||
            ''
        );
        if (model) {
          this.selectedModel.set(model);
          this.loadSubmodelsByMakeYearAndModel(model.id_modelo_anio);
        }
      }
    });
  }

  private loadSubmodelsByMakeYearAndModel(model: number): void {
    this.vehicleService.getSubmodelos(model).subscribe((submodels) => {
      this.availableSubmodels.set(submodels);
    });
  }

  private loadEnginesByMakeYearAndModel(
    idModelo: number,
    descripcionSubmodelo: string
  ): void {
    this.vehicleService
      .getMotores(idModelo, descripcionSubmodelo)
      .subscribe((engines) => {
        this.availableEngines.set(engines);
      });
  }

  toggleMobileFilters(): void {
    this.isMobileFiltersOpen.set(!this.isMobileFiltersOpen());
  }

  saveVehicle(): void {
    const year = this.selectedYear();
    const make = this.selectedMake();
    const model = this.selectedModel();
    const submodel = this.selectedSubmodel();
    const engine = this.selectedEngine();

    const userData = this.emailValidationService.getCurrentUser();

    if (!year || !make || !model || !engine || !submodel || this.isSaving()) {
      return;
    }

    // Mostrar estado de carga
    this.isSaving.set(true);

    const vehicle: Auto = {
      anio: year.anio,
      marca: make.descripcion,
      modelo: model.descripcion,
      motor: engine.descripcion,
      alias: `${year.anio} ${make.descripcion} ${model.descripcion}`,
      predeterminado: true, // Hacer que sea el vehículo por defecto
      idUsuarioDigital: userData?.idUsuarioDigital || 0,
      id: 0,
      vin: null,
      placa: null,
      idSubmodeloMotor: engine.id_submodelo_motor,
      vehicleEngineConfig: engine.vehicle_to_engine_config,
      fechaCreacion: new Date().toISOString(),
      activo: true,
    };

    this.vehicleService.add(vehicle);

    // Pequeña pausa para mostrar el estado de carga y luego recargar
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
}
